import { Component, Host, h, Prop, Listen, State, Element, Watch } from '@stencil/core';
import { isPlatform } from '@baloise/ds-core';

@Component({
  tag: 'bam-back-teaser',
  styleUrl: 'bam-back-teaser.scss',
  shadow: false,
})
export class BamBackTeaser {
  @Element() el: HTMLElement;

  /**
   * Defines the type of the insurance
   */
  @Prop() type: string = '';

  /**
   * Defines the prio of each insurance
   */
  @Prop() prio: number = 0;

  /**
   * Defines the expiration days of the cookie
   */
  @Prop() cookieExpire: number = 30;

  /**
   * Defines the url of the server request
   */
  @Prop() url: string = '';

  /**
   * Icon url coming from fetch in bam-back-teaser-list
   */
  @Prop() icon: string = '';

  /**
   * Button destination url coming from fetch in bam-back-teaser-list - for the first (zurück zur Offerte) button
   */
  @Prop() btnUrlFirstPart: string = '';

  /**
   * Url destination coming from fetch in bam-back-teaser-list - for the NEW (neu berechnen) button
   */
  @Prop() btnCalculationLink: string = '';

  /**
   * Custom text coming from fetch in bam-back-teaser-list
   */
  @Prop() customText: string = '';

  /**
   * If `true` the back teaser is block displayed.
   */
  @Prop({ mutable: true, reflect: true }) value = true;

  /**
   * If `true` the back teaser has the class active and slides in
   */
  @Prop({ mutable: true, reflect: true }) active = false;

  /**
   * The quote id set in bam-back-teaser-list coming from the higher type
   * insurance in local storage. It will be added to the higher prio type insurance button
   * in order to proceed with that insurance
   */
  @Prop() quoteId?: string = '';

  /**
   * The text content of the second higher prio insurance link at the bottom of the back teaser
   * Optional: there could be no other insurance other than the actual one
   * Used for its link title attribute as well
   */
  @Prop() secondPrioLinkText?: string;

  /**
   * The link href of the second higher prio insurance link at the bottom of the back teaser
   * Optional: there could be no other insurance other than the actual one
   */
  @Prop() secondPrioLink?: string;

  /**
   * The default icon for the mobile button
   */
  @Prop() mobileIconDefault: string;

  /**
   * The default alt-text for the mobile button icon
   */
  @Prop() mobileIconAltText: string;

  @State() isTouch: boolean = false;
  @State() teaserHidden: boolean = true;
  @State() hasConsent: boolean = window['OnetrustActiveGroups']?.includes('C0003');

  private mobileIconWrapper;

  @Watch('active')
  async watchActiveHandler(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue && newValue) {
      const iconEl = this.el.querySelector('[data-js-item="back-teaser-icon"]') as HTMLElement;
      const iconImg = !!iconEl ? (iconEl.querySelector('bam-image') as HTMLBamImageElement) : null;
      const defaultIconUrl = !!iconImg ? iconImg.image : '';
      const customTextEl = this.el.querySelector('[data-js-item="custom-text"]') as HTMLElement;
      const separatorTextEl = this.el.querySelector('[data-js-item="separator"]') as HTMLElement;
      const nextLinkWrapperEl = this.el.querySelector('[data-js-item="next-prio-link-wrapper"]') as HTMLElement;
      const nextLinkEl = !!nextLinkWrapperEl ? (nextLinkWrapperEl.querySelector('a') as HTMLElement) : null;
      const btnsWrapperEl = this.el.querySelector('[data-js-item="back-teaser-btns-wrapper"]') as HTMLElement;
      const btnOpenBack = this.el.querySelector('[data-js-item="back-teaser-btn"]').querySelector('bal-button');
      const btnOpenNew = this.el.querySelector('[data-js-item="back-teaser-btn-open"]').querySelector('bal-button');
      this.mobileIconWrapper = this.teaserHidden && this.isTouch ? this.el.querySelector('.back-teaser__mobile-icon-wrapper').querySelector('bam-image') : null;
      const secondLinkContentIsThere =
        !!nextLinkWrapperEl &&
        !!nextLinkEl &&
        !!this.secondPrioLink &&
        this.secondPrioLink !== '' &&
        !!this.secondPrioLinkText &&
        this.secondPrioLinkText !== '' &&
        !!btnOpenBack &&
        !!separatorTextEl &&
        !!btnsWrapperEl;

      if (secondLinkContentIsThere) {
        btnOpenBack.classList.add('mb-x-small');
        separatorTextEl.remove();
        btnsWrapperEl.appendChild(separatorTextEl);
        nextLinkEl.setAttribute('href', this.secondPrioLink);
        nextLinkEl.querySelector('span').textContent = this.secondPrioLinkText;
        nextLinkEl.setAttribute('title', this.secondPrioLinkText);
        nextLinkWrapperEl.removeAttribute('aria-hidden');
        nextLinkWrapperEl.classList.remove('hidden');
      }

      if (!!iconImg) {
        iconImg.image = this.icon ? this.icon : defaultIconUrl;
        if (this.teaserHidden && this.isTouch && this.icon && !!this.mobileIconWrapper) {
          //TODO check resize
          this.mobileIconWrapper.image = this.icon ? this.icon : this.mobileIconDefault;
        }
      }

      if (!!customTextEl) {
        customTextEl.innerText = this.customText;
      }

      if (!!btnOpenBack) {
        btnOpenBack.href = this.btnUrlFirstPart + this.quoteId;
      }

      if (!!btnOpenNew) {
        btnOpenNew.href = this.btnCalculationLink;
      }
    }
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.setIsTouch();
  }

  @Listen('consentChange', { target: 'window' })
  onConsentChange() {
    this.hasConsent = window['OnetrustActiveGroups']?.includes('C0003');
  }

  setIsTouch() {
    this.isTouch = isPlatform(window, 'touch');
  }

  connectedCallback() {
    this.setIsTouch();
  }

  toggleTeaser() {
    this.teaserHidden = !this.teaserHidden;
  }

  render() {
    return (
      <Host style={{ display: this.value && this.hasConsent ? 'block' : 'none' }} class={{ 'is-active': this.active }}>
        <div style={{ display: !this.isTouch || !this.teaserHidden ? 'block' : 'none' }} class={{ 'back-teaser__content-mobile': this.isTouch && !this.teaserHidden }}>
          <slot></slot>
        </div>
        {this.teaserHidden && this.isTouch && (
          <bal-button square={true} class="block mt-x-small back-teaser__mobile-icon-wrapper" color="light" onClick={() => this.toggleTeaser()}>
            <bam-image image={this.mobileIconDefault} alt-image={this.mobileIconAltText} class="back-teaser__mobile-icon"></bam-image>
          </bal-button>
        )}
      </Host>
    );
  }
}
